<template>
  <section class="privacy-policy">
    <h1>Privacy Policy</h1>
    <p>
      <strong>Last updated:</strong>
      25-06-2025
    </p>
    <p>
      Funnelflow ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy
      explains how we collect, use, store, and share your personal information when you use our
      services, including our website, forms, and integrations.
    </p>
    <p>
      <strong>Contact Information:</strong>
      <br />
      Funnelflow
      <br />
      Bruistensingel 500, 5232AH 's-Hertogenbosch, The Netherlands
      <br />
      info@getfunnelflow.com
    </p>
    <h2>1. What Data We Collect</h2>
    <ul>
      <li>
        <strong>Identity and Contact Data:</strong>
        Name, email address
      </li>
      <li>
        <strong>Technical Data:</strong>
        IP address
      </li>
      <li>
        <strong>Payment Data:</strong>
        Payment information (processed via Stripe and Moneybird)
      </li>
      <li>
        <strong>Scheduling Data:</strong>
        Information related to scheduling and calendar events (including Google Calendar
        integrations)
      </li>
      <li>
        <strong>Form Data:</strong>
        Information you submit via our forms
      </li>
    </ul>
    <p>
      We collect this data through forms on our website, cookies and similar technologies, analytics
      tools, and third-party integrations (e.g., Google Calendar).
    </p>
    <h2>2. How We Use Your Data</h2>
    <ul>
      <li>To create and manage your account</li>
      <li>To process payments and manage billing</li>
      <li>To provide, maintain, and improve our services</li>
      <li>To facilitate scheduling and calendar integrations</li>
      <li>To analyze usage and improve user experience</li>
      <li>To communicate with you about your account or our services</li>
      <li>To process form submissions and deliver requested services</li>
    </ul>
    <p>
      We do
      <strong>not</strong>
      use your data for automated decision-making or profiling.
    </p>
    <h2>3. Sharing Your Data</h2>
    <p>
      We only share your data with third parties when necessary and with your consent, where
      required. These third parties include:
    </p>
    <ul>
      <li>
        <strong>Analytics Providers:</strong>
        Plausible, Google Tag Manager, Microsoft Clarity
      </li>
      <li>
        <strong>Payment Processors:</strong>
        Stripe, Moneybird
      </li>
      <li>
        <strong>Cloud and Hosting Providers:</strong>
        Render.com (Frankfurt, Germany), Supabase
      </li>
    </ul>
    <p>We do not sell your personal data to third parties.</p>
    <h2>4. Data Storage and Security</h2>
    <p>
      Your data is stored securely on Render.com servers in Frankfurt, Germany, and on Supabase. We
      use encryption and access controls to protect your data from unauthorized access, disclosure,
      alteration, or destruction.
    </p>
    <h2>5. Your Rights</h2>
    <p>You have the right to:</p>
    <ul>
      <li>Access your personal data</li>
      <li>Correct inaccurate or incomplete data</li>
      <li>Delete your data at any time</li>
    </ul>
    <p>
      To exercise these rights, please email us at
      <a href="mailto:support@getfunnelflow.com">support@getfunnelflow.com</a>
      .
    </p>
    <h2>6. Cookies and Tracking</h2>
    <p>We use cookies and similar technologies for:</p>
    <ul>
      <li>Analytics (Plausible, Google Tag Manager, Microsoft Clarity)</li>
      <li>Service functionality and improvement</li>
    </ul>
    <p>You can manage your cookie preferences through your browser settings.</p>
    <h2>7. International Data Transfers</h2>
    <p>
      We do
      <strong>not</strong>
      transfer your personal data outside the European Economic Area (EEA).
    </p>
    <h2>8. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. We will notify you of any significant
      changes by updating this document and, where appropriate, by email.
    </p>
    <h2>9. Contact</h2>
    <p>
      If you have any questions or concerns about this Privacy Policy or your data, please contact
      us at
      <a href="mailto:info@getfunnelflow.com">info@getfunnelflow.com</a>
      .
    </p>
    <p>
      <em>
        This Privacy Policy is intended to comply with the General Data Protection Regulation (GDPR)
        and other applicable privacy laws.
      </em>
    </p>
  </section>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';

  /**
   * PrivacyPolicy
   *
   * This page displays the privacy policy for Funnelflow, outlining data collection, usage, and user rights.
   */
  export default defineComponent({
    name: 'PrivacyPolicy',
    components: {},
    props: {},
    emits: {},
    setup() {
      // No setup logic required
      return {};
    },
  });
</script>

<style scoped lang="scss">
  .privacy-policy {
    max-width: 800px;
    margin: 2rem auto;
    padding: 2rem;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    h1 {
      margin-bottom: 1.5rem;
    }
    h2 {
      margin-top: 2rem;
      margin-bottom: 1rem;
    }
    ul {
      margin-left: 1.5rem;
      margin-bottom: 1rem;
    }
    p {
      margin-bottom: 1rem;
    }
  }
</style>
